import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import i18n from '../i18n';

// ★ 2026-07-29 refactor — constants moved to ./stageTransition/constants.js
// ★ Fase 59.P2 — field renderers estratti in componenti dedicati (split oversize)
import { AppuntamentoFissatoFields } from './stageTransition/AppuntamentoFissatoFields';
import { NoShowFields } from './stageTransition/NoShowFields';
import { PropostaInviataFields } from './stageTransition/PropostaInviataFields';
import { ChiusoFields } from './stageTransition/ChiusoFields';
import { ChiusoPartnerFields } from './stageTransition/ChiusoPartnerFields';
import { PendingShowFields } from './stageTransition/PendingShowFields';
import { PersoFields } from './stageTransition/PersoFields';

/**
 * StageTransitionModal — Universal modal for moving a lead between pipeline stages.
 *
 * Renders the fields required by the target stage (see pipeline_v2.STAGE_REQUIRED_FIELDS).
 * If the target stage has no required fields (e.g. 'chiamato', 'lead', 'show'), the
 * caller can choose to skip the modal entirely and call /transition directly.
 */
export default function StageTransitionModal({
  open,
  onOpenChange,
  lead,
  toStage,
  toStageLabel,
  onConfirm,
}) {
  const initialState = useMemo(() => {
    const cf = lead?.custom_fields || {};
    return {
      // appuntamento_fissato
      profile_type: cf.profile_type || '',
      hcf_archetype: cf.hcf_archetype || '',
      appointment_at: cf.appointment_at?.slice(0, 16) || '',
      budget_range: cf.budget_range || '',
      timeframe: cf.timeframe || '',
      notes_profile: cf.notes_profile || '',
      // ★ 2026-07-09 (richiesta utente): senior selector per Associate quando
      // fissa appuntamento — l'Associate ora sceglie IL SENIOR che gestirà la call.
      assigned_senior_id: cf.assigned_senior_id || cf.senior_agent_id || '',
      // no_show
      no_show_action: '',
      no_show_reschedule_at: '',
      no_show_archive_reason: '',
      no_show_archive_note: '',
      // proposta_inviata
      proposta_url: cf.proposta_url || '',
      proposta_sent_at: cf.proposta_sent_at?.slice(0, 16) || new Date().toISOString().slice(0, 16),
      // ★ 2026-07-03: importo proposta (OBBLIGATORIO) per KPI dashboard
      proposal_amount_eur: cf.proposal_amount_eur ? String(cf.proposal_amount_eur) : '',
      // chiuso
      contract_url: cf.contract_url || '',
      invoice_url: cf.invoice_url || '',
      payment_status: cf.payment_status || 'pending',
      contabile_note: cf.contabile_note || '',
      closed_amount_eur: cf.closed_amount_eur || '',
      // ★ 2026-06-25: piano pagamenti (secco o rate)
      payment_plan: {
        payment_type: cf.payment_type || 'secco',
        currency: 'EUR',
        installments: [{ amount: '', due_date: new Date().toISOString().slice(0, 10), notes: 'Anticipo' }],
      },
      // ★ 2026-07-29: chiuso_partner (tipo partnership)
      partnership_type: cf.partnership_type || '',
      partnership_notes: cf.partnership_notes || '',
      // ★ 2026-07-29: pending_show (4 checkbox + weeks)
      pending_show_reasons: [],
      pending_show_weeks: '',
      pending_show_notes: '',
      // perso
      lost_reason: '',
      lost_note: '',
    };
  // Re-initialise when lead or toStage changes.
  // ★ 2026-07-29 (hooks fix): dep on `lead?.custom_fields` triggers on every
  // partial update from parent (e.g. after a nested field save). Instead we
  // key on `lead?.id + updated_at` so we re-init only when a NEW lead loads
  // or a full reload happened. Values read via closure inside the memo body.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, lead?.updated_at, toStage, open]);

  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);
  // ★ 2026-07-09: caricamento senior agents per il selector
  const [seniorsList, setSeniorsList] = useState([]);
  // ★ 2026-08-03 (Fase 31 D) — Libreria proposte + selezione per creazione ordine
  const [proposalsLibrary, setProposalsLibrary] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);   // multi
  const [selectedPaths, setSelectedPaths] = useState([]);           // multi
  const [selectedProposalIds, setSelectedProposalIds] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);

  // ★ 2026-08-04 (Fase 33.1) — Selezione proposte chiuse (per Pagamenti)
  // Solo quando toStage in {chiuso_cliente, chiuso_partner}. Struttura:
  //   sentProposals: [{ order_id, proposal_id, name, price_eur, ... }]
  //   closedPayments: { [proposal_id]: { checked, amount, method, paid_at, notes, unit_id, unit_code, unit_project_slug } }
  const [sentProposals, setSentProposals] = useState([]);
  const [loadingSent, setLoadingSent] = useState(false);
  const [closedPayments, setClosedPayments] = useState({});

  // ★ 2026-08-14 (Fase 53) — Catalogo unità disponibili cross-project per
  // collegare la chiusura a un codice immobile → il primo pagamento imposta
  // automaticamente lo stato dell'unità a `sold`.
  const [availableUnits, setAvailableUnits] = useState([]);

  useEffect(() => {
    // Carica lista senior una sola volta all'apertura del modal per lo stage 'appuntamento_fissato'
    if (open && toStage === 'appuntamento_fissato' && seniorsList.length === 0) {
      api.get('/agents')
        .then((r) => {
          const seniors = (r.data || []).filter((a) => a.is_active !== false && (
            a.is_senior === true
            || a.is_ceo_fallback === true
            || (a.role_label || '').toLowerCase().includes('senior')
            || (a.role || '').toLowerCase().includes('senior')
            || (a.role || '') === 'ceo'
            || (a.role || '') === 'co_ceo'
          ));
          setSeniorsList(seniors);
        })
        .catch(() => setSeniorsList([]));
    }
  }, [open, toStage, seniorsList.length]);

  // Reset form when the modal is re-opened
  React.useEffect(() => {
    if (open) setForm(initialState);
  }, [open, initialState]);

  // ★ 2026-08-03 (Fase 31 D) — Carica libreria proposte quando si apre il modal per proposta_inviata
  useEffect(() => {
    if (open && toStage === 'proposta_inviata') {
      setLoadingLibrary(true);
      api.get('/proposals-library', { params: { is_active: true } })
        .then((r) => setProposalsLibrary(r.data.items || []))
        .catch(() => setProposalsLibrary([]))
        .finally(() => setLoadingLibrary(false));
      // reset selezioni ogni apertura
      setSelectedCountries([]);
      setSelectedPaths([]);
      setSelectedProposalIds([]);
    }
  }, [open, toStage]);

  // ★ 2026-08-04 (Fase 33.1) — Carica proposte inviate al lead quando si apre modal
  // per chiuso_cliente / chiuso_partner. L'admin poi seleziona quali sono state
  // effettivamente chiuse e per ognuna inserisce importo + metodo + data.
  useEffect(() => {
    const isClosing = toStage === 'chiuso_cliente' || toStage === 'chiuso_partner' || toStage === 'chiuso';
    if (open && isClosing && lead?.id) {
      setLoadingSent(true);
      api.get(`/proposal-orders/by-lead/${lead.id}`)
        .then((r) => {
          const items = r.data.items || [];
          setSentProposals(items);
          // pre-fill defaults
          const today = new Date().toISOString().slice(0, 10);
          const initMap = {};
          items.forEach((it) => {
            initMap[it.proposal_id] = {
              checked: false,
              amount: String(it.price_eur ?? ''),
              method: it.payment_method || '',
              paid_at: today,
              notes: '',
              // ★ 2026-08-14 (Fase 53) — Selezione unità immobiliare (opzionale)
              // ★ 2026-08-28 — Prefill automatico dall'unità COLLEGATA alla proposta
              unit_id: it.unit_id || '',
              unit_code: it.unit_code || '',
              unit_project_slug: it.unit_project_slug || '',
            };
          });
          setClosedPayments(initMap);
        })
        .catch(() => setSentProposals([]))
        .finally(() => setLoadingSent(false));

      // ★ 2026-08-14 (Fase 53) — Carica catalogo unità disponibili cross-project
      api.get('/property-units/catalog/available')
        .then((r) => setAvailableUnits(r.data?.items || []))
        .catch(() => setAvailableUnits([]));
    }
  }, [open, toStage, lead?.id]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleConfirm = async () => {
    setSaving(true);
    try {
      // Build metadata depending on target stage
      let metadata = {};
      if (toStage === 'appuntamento_fissato') {
        // ★ 2026-07-09: validazione senior obbligatorio
        if (!form.assigned_senior_id) {
          toast.error(i18n.t('stModal.seniorRequired', 'Seleziona il Senior che gestirà l\'appuntamento'));
          setSaving(false);
          return;
        }
        if (!form.appointment_at) {
          toast.error(i18n.t('stModal.apptDateRequired', 'Inserisci la data dell\'appuntamento'));
          setSaving(false);
          return;
        }
        if (!form.profile_type || !form.hcf_archetype) {
          toast.error(i18n.t('stModal.profileRequired', 'Completa profilo cliente e archetipo HCF'));
          setSaving(false);
          return;
        }
        // Nome senior per snapshot (facilita audit + email/telegram)
        const seniorObj = seniorsList.find((s) => s.id === form.assigned_senior_id);
        metadata = {
          profile_type: form.profile_type,
          hcf_archetype: form.hcf_archetype,
          appointment_at: form.appointment_at ? new Date(form.appointment_at).toISOString() : '',
          budget_range: form.budget_range || null,
          timeframe: form.timeframe || null,
          notes_profile: form.notes_profile || null,
          // ★ 2026-07-09: assegnazione Senior alla transition appuntamento_fissato
          assigned_senior_id: form.assigned_senior_id,
          senior_agent_id: form.assigned_senior_id,      // alias legacy
          senior_agent_name: seniorObj?.name || null,
          senior_agent_email: seniorObj?.email || null,
        };
      } else if (toStage === 'no_show') {
        metadata = {
          no_show_action: form.no_show_action,
          no_show_reschedule_at: form.no_show_action === 'reschedule' && form.no_show_reschedule_at
            ? new Date(form.no_show_reschedule_at).toISOString()
            : null,
          no_show_archive_reason: form.no_show_action === 'archive' ? form.no_show_archive_reason : null,
          no_show_archive_note: form.no_show_action === 'archive' ? form.no_show_archive_note : null,
        };
      } else if (toStage === 'proposta_inviata') {
        // ★ 2026-07-03: importo obbligatorio per KPI potenziale fatturato
        // ★ 2026-08-03: se ci sono proposte selezionate, l'importo viene
        // pre-compilato dalla somma; l'input rimane comunque editabile.
        const amt = parseFloat(form.proposal_amount_eur);
        if (!amt || amt <= 0) {
          toast.error(i18n.t('stModal.amountRequired', 'Inserisci l\'importo della proposta per aggiornare la previsione in Dashboard'));
          setSaving(false);
          return;
        }
        if (!form.proposta_url?.trim() && selectedProposalIds.length === 0) {
          toast.error(i18n.t('stModal.linkRequired', 'Inserisci il link della proposta oppure seleziona almeno una proposta dalla libreria'));
          setSaving(false);
          return;
        }
        metadata = {
          proposta_url: form.proposta_url || '',
          proposta_sent_at: form.proposta_sent_at ? new Date(form.proposta_sent_at).toISOString() : '',
          proposal_amount_eur: amt,
          selected_countries: selectedCountries,
          selected_paths: selectedPaths,
          selected_proposal_ids: selectedProposalIds,
        };
      } else if (toStage === 'chiuso' || toStage === 'chiuso_cliente' || toStage === 'chiuso_partner') {
        // ★ 2026-06-25: prima crea/aggiorna il piano pagamenti, POI fa il transition.
        // Il backend del transition rifiuta lo stage 'chiuso' se non c'è un piano attivo.
        const plan = form.payment_plan || {};
        const installments = (plan.installments || []).filter(
          (it) => parseFloat(it.amount) > 0 && it.due_date,
        );
        if (installments.length === 0) {
          toast.error(i18n.t('stModal.installmentRequired', 'Inserisci almeno una rata con importo e data scadenza'));
          setSaving(false);
          return;
        }
        // ★ 2026-07-29: chiuso_partner richiede partnership_type
        if (toStage === 'chiuso_partner' && !form.partnership_type) {
          toast.error(i18n.t('stModal.partnershipRequired', 'Seleziona il tipo di partnership (Affiliato / Broker / Reseller / ...)'));
          setSaving(false);
          return;
        }
        // ★ 2026-08-04 (Fase 33.1): valida i campi delle proposte chiuse selezionate
        const invalidProposal = Object.entries(closedPayments).find(([, v]) => {
          if (!v.checked) return false;
          const amt = parseFloat(v.amount);
          if (!amt || amt <= 0) return true;
          if (!(v.method || '').trim()) return true;
          if (!v.paid_at) return true;
          return false;
        });
        if (invalidProposal) {
          toast.error(i18n.t('stModal.closedFieldsRequired', 'Compila importo, metodo pagamento e data per ogni proposta chiusa selezionata'));
          setSaving(false);
          return;
        }
        try {
          await api.post(`/leads/${lead.id}/payment-plan`, {
            payment_type: plan.payment_type || 'secco',
            currency: plan.currency || 'EUR',
            installments: installments.map((it) => ({
              amount: parseFloat(it.amount),
              due_date: it.due_date,
              notes: it.notes || null,
            })),
          });
        } catch (e) {
          const msg = e?.response?.data?.detail || i18n.t('stageModal.planSaveError', 'Errore salvataggio piano pagamenti');
          toast.error(msg);
          setSaving(false);
          return;
        }
        // Il totale del piano va anche nel transition metadata per audit/log
        const totalAmount = installments.reduce((acc, it) => acc + (parseFloat(it.amount) || 0), 0);
        metadata = {
          payment_type: plan.payment_type,
          payment_plan_set: true,
          closed_amount_eur: totalAmount,
          contabile_note: form.contabile_note || null,
        };
        if (toStage === 'chiuso_partner') {
          metadata.partnership_type = form.partnership_type;
          metadata.partnership_notes = form.partnership_notes || null;
        }
      } else if (toStage === 'pending_show') {
        // ★ 2026-07-29: pending_show → almeno 1 checkbox + se "rivedersi", weeks
        const reasons = form.pending_show_reasons || [];
        if (reasons.length === 0) {
          toast.error(i18n.t('stModal.pendingReasonRequired', 'Seleziona almeno un motivo per il Pending Show'));
          setSaving(false);
          return;
        }
        if (reasons.includes('rivedersi_x_settimane')) {
          const w = parseInt(form.pending_show_weeks, 10);
          if (!w || w <= 0) {
            toast.error(i18n.t('stModal.weeksRequired', 'Inserisci il numero di settimane per il follow-up'));
            setSaving(false);
            return;
          }
        }
        metadata = {
          pending_show_reasons: reasons,
          pending_show_weeks: reasons.includes('rivedersi_x_settimane') ? parseInt(form.pending_show_weeks, 10) : null,
          pending_show_notes: form.pending_show_notes || null,
        };
      } else if (toStage === 'perso') {
        metadata = {
          lost_reason: form.lost_reason,
          lost_note: form.lost_note || null,
        };
      }
      await onConfirm({ to_stage: toStage, metadata });
      // ★ 2026-08-04 (Fase 33.1) — Se la chiusura ha selezionato proposte,
      // crea i record `proposal_payments` DOPO la transizione stage.
      if ((toStage === 'chiuso_cliente' || toStage === 'chiuso_partner' || toStage === 'chiuso') && lead?.id) {
        const selectedItems = Object.entries(closedPayments)
          .filter(([, v]) => v.checked)
          .map(([proposal_id, v]) => {
            const src = sentProposals.find((sp) => sp.proposal_id === proposal_id) || {};
            return {
              order_id: src.order_id || null,
              proposal_id,
              amount_eur: parseFloat(v.amount) || 0,
              payment_method: (v.method || '').trim(),
              paid_at: v.paid_at,
              notes: v.notes || null,
              // ★ 2026-08-14 (Fase 53) — Link a immobile → auto-sold on payment
              unit_id: v.unit_id || null,
              unit_code: v.unit_code || null,
              unit_project_slug: v.unit_project_slug || null,
              proposal_snapshot: {
                name: src.name,
                country: src.country,
                path: src.path,
                price_eur: src.price_eur,
                payment_method: src.payment_method,
              },
            };
          })
          .filter((it) => it.amount_eur > 0 && it.payment_method && it.paid_at);
        if (selectedItems.length > 0) {
          try {
            const { data } = await api.post('/payments/bulk', {
              lead_id: lead.id,
              items: selectedItems,
            });
            toast.success(i18n.t('stModal.paymentsRecorded', 'Registrati {{n}} pagamenti — visibili in "Pagamenti"', { n: data.created }));
          } catch (err) {
            toast.warning(err?.response?.data?.detail || i18n.t('stModal.paymentsWarn', 'Chiusura OK ma errore registrazione pagamenti'));
          }
        }
      }
      // ★ 2026-08-03 (Fase 31 D) — Dopo la transizione a proposta_inviata,
      // se il senior ha selezionato proposte dalla libreria, crea l'ordine.
      if (toStage === 'proposta_inviata' && selectedProposalIds.length > 0 && lead?.id) {
        try {
          const items = selectedProposalIds.map((pid) => ({ proposal_id: pid }));
          const { data: order } = await api.post('/proposal-orders', {
            lead_id: lead.id,
            countries_selected: selectedCountries,
            paths_selected: selectedPaths,
            items,
          });
          toast.success(i18n.t('stModal.orderCreated', 'Ordine proposta creato ({{n}} email in coda)', { n: items.length }), {
            description: i18n.t('stModal.orderCreatedDesc', 'Gli admin CEO possono inviare le email dalla pagina Ordini proposte.'),
          });
          // Notifica il parent per aprire eventualmente il nuovo ordine
          if (typeof window !== 'undefined' && order?.id) {
            window.dispatchEvent(new CustomEvent('proposal-order-created', { detail: order }));
          }
        } catch (err) {
          toast.warning(err?.response?.data?.detail || i18n.t('stModal.orderWarn', 'Transizione OK ma errore creazione ordine proposta'));
        }
      }
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  // ── Field renderers per stage — ★ Fase 59.P2: estratti in componenti
  // dedicati sotto ./stageTransition/ (split file oversize, zero cambi di
  // comportamento). Questo file ora contiene solo stato + validazione + JSX.
  // ★ 2026-08-04 (Fase 33.1) — aggiorna un campo di una proposta chiusa
  const setPay = (pid, k, v) =>
    setClosedPayments((s) => ({ ...s, [pid]: { ...(s[pid] || {}), [k]: v } }));

  const chiusoProps = {
    form, set, sentProposals, loadingSent, closedPayments, setPay, availableUnits,
  };

  // ── Validation per stage
  const isValid = useMemo(() => {
    switch (toStage) {
      case 'appuntamento_fissato':
        return !!(form.profile_type && form.hcf_archetype && form.appointment_at);
      case 'no_show':
        if (form.no_show_action === 'reschedule') return !!form.no_show_reschedule_at;
        if (form.no_show_action === 'archive') return !!form.no_show_archive_reason;
        return false;
      case 'proposta_inviata':
        return !!(form.proposta_url && form.proposta_sent_at);
      case 'pending_show': {
        // ★ 2026-07-29: almeno 1 reason; se rivedersi_x_settimane, weeks obbligatorio
        const reasons = form.pending_show_reasons || [];
        if (reasons.length === 0) return false;
        if (reasons.includes('rivedersi_x_settimane')) {
          const w = parseInt(form.pending_show_weeks, 10);
          if (!w || w <= 0) return false;
        }
        return true;
      }
      case 'chiuso':
      case 'chiuso_cliente':
      case 'chiuso_partner': {
        const plan = form.payment_plan || {};
        const valid = (plan.installments || []).filter(
          (it) => parseFloat(it.amount) > 0 && it.due_date,
        );
        if (valid.length === 0) return false;
        if (toStage === 'chiuso_partner' && !form.partnership_type) return false;
        return true;
      }
      case 'perso':
        return !!form.lost_reason;
      default:
        return true;
    }
  }, [toStage, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="stage-transition-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {i18n.t('stModal.moveLead', 'Sposta lead')} → <Badge variant="secondary">{toStageLabel}</Badge>
          </DialogTitle>
          <DialogDescription>
            {lead?.name} · {lead?.email || lead?.phone || '—'}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {toStage === 'appuntamento_fissato' && (
            <AppuntamentoFissatoFields form={form} set={set} seniorsList={seniorsList} />
          )}
          {toStage === 'no_show' && <NoShowFields form={form} set={set} />}
          {toStage === 'proposta_inviata' && (
            <PropostaInviataFields
              form={form}
              set={set}
              proposalsLibrary={proposalsLibrary}
              loadingLibrary={loadingLibrary}
              selectedCountries={selectedCountries}
              setSelectedCountries={setSelectedCountries}
              selectedPaths={selectedPaths}
              setSelectedPaths={setSelectedPaths}
              selectedProposalIds={selectedProposalIds}
              setSelectedProposalIds={setSelectedProposalIds}
            />
          )}
          {toStage === 'pending_show' && <PendingShowFields form={form} set={set} />}
          {(toStage === 'chiuso' || toStage === 'chiuso_cliente') && <ChiusoFields {...chiusoProps} />}
          {toStage === 'chiuso_partner' && <ChiusoPartnerFields {...chiusoProps} />}
          {toStage === 'perso' && <PersoFields form={form} set={set} />}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving} data-testid="stage-modal-cancel">
            {i18n.t('common.cancel', 'Annulla')}
          </Button>
          <Button onClick={handleConfirm} disabled={!isValid || saving} data-testid="stage-modal-confirm">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            {i18n.t('common.confirm', 'Conferma')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Stages that REQUIRE a modal before transitioning (have required metadata).
export const STAGES_WITH_MODAL = new Set([
  'appuntamento_fissato',
  'no_show',
  'pending_show',
  'proposta_inviata',
  'chiuso',
  'chiuso_cliente',
  'chiuso_partner',
  'perso',
]);
